import { ObjectState } from "@homebound/form-state";
import { useCallback } from "react";
import { BidContractType, BidScopeSource, SaveBidPackageInput } from "src/generated/graphqlTypes";
import { useSavePlanBasedBidPackagesMutation } from "src/routes/bids/plan-based-bid-package/PlanBasedBidPackagePage.generated";
import {
  INTERNAL_ESTIMATE_ID,
  mapToBidPlanGroupsInput,
  PlanBasedBidPackageFormObjectState,
  PlanBasedBidPackageFormState,
} from "src/routes/bids/plan-based-bid-package/utils";
import { createPlanBasedBidPackageEditUrl } from "src/RouteUrls";
import { StringParam, useQueryParams } from "use-query-params";

/**
 * The single write path for the plan-based bid wizard's form. `saveBidPackage`'s `bidPlanGroups` input is exhaustive,
 * so anything that persists groups behind this form's back leaves it stale.
 */
export function useSavePlanBasedBidPackage(formState: PlanBasedBidPackageFormObjectState) {
  const [saveBidPackages, { loading: saving }] = useSavePlanBasedBidPackagesMutation();
  const [{ projectId }] = useQueryParams({ projectId: StringParam });

  const persist = useCallback(async () => {
    const result = await saveBidPackages({ variables: { input: [mapToSaveBidPackagesInput(formState)] } });
    const saved = result.data?.saveBidPackages.bidPackages?.first;
    const groupId = saved?.bidPackageGroup?.id;
    if (saved && groupId) {
      // Fold the saved server response back into the form. `update` re-runs the form's `init.map`
      // (mapBidPackageToForm) and reconciles field-by-field, so it both picks up the server-assigned
      // ids and marks the form clean (this also commits form changes so the stepper won't prompt about
      // unsaved changes).
      formState.update(saved);
      window.history.replaceState(null, "", createPlanBasedBidPackageEditUrl(groupId, projectId));
    }
    return saved;
  }, [formState, saveBidPackages, projectId]);

  return { persist, saveBidPackages, saving };
}

function mapToSaveBidPackagesInput(formState: ObjectState<PlanBasedBidPackageFormState>): SaveBidPackageInput {
  const {
    bidPackageId,
    bidPackageGroupId,
    marketId,
    projects,
    costCodes,
    costType,
    internalEstimateType,
    productOfferings,
    requests,
    bidPlanGroups,
  } = formState.value;
  const internalEstimate = requests?.find((r) => r.tradePartnerId === INTERNAL_ESTIMATE_ID);
  return {
    id: bidPackageId ?? undefined,
    bidPackageGroupId: bidPackageGroupId ?? undefined,
    projects: marketId && (projects ?? []).isEmpty ? [marketId] : (projects ?? []),
    costCodes: costCodes ?? [],
    costType: costType ?? undefined,
    scopeSource: BidScopeSource.ProductOffering,
    isUnitBased: false,
    isPlanBasedV2: true,
    internalEstimateBidContract: internalEstimate
      ? {
          id: internalEstimate.id,
          isInternalEstimate: true,
          internalEstimateType,
          archive: internalEstimate.archive,
          type: BidContractType.UserCreatedGroupBased,
        }
      : undefined,
    productOfferings: productOfferings ?? undefined,
    requests: requests?.nonEmpty ? requests.filter((r) => r.tradePartnerId !== INTERNAL_ESTIMATE_ID) : undefined,
    bidPlanGroups: bidPlanGroups ? mapToBidPlanGroupsInput(bidPlanGroups) : undefined,
  };
}
